// Kurum Kanalı Agentı — Faz 1
//
// Bu dosya kurum kanalları için tek kanonik registry ve salt-okunur çözümleme
// katmanıdır. Kayıtlar canlı web taramasıyla üretilmez; yalnız mevcut kaynaklı
// veriler taşınmıştır. Kullanıcı tarafından girilen adresler bu registry'ye yazılmaz.

export const CHANNEL_TYPES = Object.freeze({
  EMAIL: "email",
  OFFICIAL_FORM: "official_form",
  ONLINE_ACCOUNT: "online_account",
  OFFICIAL_CONTACT_PAGE: "official_contact_page",
  PHONE: "phone",
  POSTAL: "postal",
  ESCALATION: "escalation",
  UNKNOWN: "unknown",
});

export const CHANNEL_STATUSES = Object.freeze({
  VERIFIED: "verified",
  STALE: "stale",
  PENDING_REVIEW: "pending_review",
  REJECTED: "rejected",
  UNAVAILABLE: "unavailable",
});

export const CHANNEL_CONFIDENCE = Object.freeze({
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
});

export const CHANNEL_REVIEW_INTERVAL_DAYS = 90;

export const CHANNEL_PURPOSES = Object.freeze({
  iptal: "iptal",
  cancellation: "iptal",
  fatura: "fatura",
  billingDispute: "fatura",
  iade: "iade",
  returnExchange: "iade",
  hakem: "hakem",
  consumerArbitration: "hakem",
});

const REVIEWER = "feshet_registry_review";
const CANCELLATION = ["iptal"];

function createChannel({
  id,
  type,
  value,
  label,
  issueTypes = CANCELLATION,
  status = CHANNEL_STATUSES.VERIFIED,
  sourceUrl,
  sourceTitle,
  officialDomain,
  verifiedAt,
  verifiedBy = REVIEWER,
  confidence = CHANNEL_CONFIDENCE.HIGH,
  acceptsAttachments = null,
  requiredFields = [],
  instructions = "",
  priority = 100,
  alternativeOnly = false,
  reviewReason = null,
  valueVerifiedFromSource = true,
  purpose = "direct_cancellation",
  serviceScopes = [],
  requirements = [],
  showOnResult = false,
  billingProviders = [],
  actionUrl = null,
  commerceProblems = [],
  purchaseAccountModes = [],
}) {
  return {
    id,
    type,
    value,
    label,
    issueTypes,
    status,
    sourceUrl,
    sourceTitle,
    officialDomain,
    verifiedAt,
    verifiedBy,
    confidence,
    acceptsAttachments,
    requiredFields,
    instructions,
    priority,
    alternativeOnly,
    reviewReason,
    valueVerifiedFromSource,
    purpose,
    serviceScopes,
    requirements,
    showOnResult,
    billingProviders,
    actionUrl,
    commerceProblems,
    purchaseAccountModes,
  };
}

function createEscalationChannel({
  id,
  value,
  label,
  sourceTitle,
  verifiedAt,
  priority,
  serviceScopes = [],
  showOnResult = false,
}) {
  return createChannel({
    id,
    type: CHANNEL_TYPES.ESCALATION,
    value,
    label,
    sourceUrl: value,
    sourceTitle,
    officialDomain: "turkiye.gov.tr",
    verifiedAt,
    priority,
    alternativeOnly: true,
    instructions: "Bu kanal ilk kurum başvurusu değil, alternatif veya üst başvuru yoludur.",
    purpose: "escalation",
    serviceScopes,
    showOnResult,
  });
}

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.values(value).forEach(deepFreeze);
  return Object.freeze(value);
}

export const INSTITUTION_CHANNELS = deepFreeze([
  {
    companyId: "d-smart",
    companyName: "D-Smart",
    aliases: ["D-Smart", "D Smart", "D-Smart Net"],
    subscriptionLabel: "Abonelik numarası",
    officialDomains: ["dsmart.com.tr", "turkiye.gov.tr"],
    issueTypes: ["iptal", "fatura", "iade"],
    notes: "Resmî destek sayfası genel talepler içindir; doğrudan fesih kanalı olarak etiketlenmez.",
    channels: [
      createChannel({
        id: "d-smart-official-support",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.dsmart.com.tr/destek",
        label: "D-Smart resmî destek ve talep formu",
        issueTypes: ["iptal", "fatura", "iade"],
        purpose: "general_support",
        sourceUrl: "https://www.dsmart.com.tr/destek",
        sourceTitle: "D-Smart Destek",
        officialDomain: "dsmart.com.tr",
        verifiedAt: "2026-07-20",
        verifiedBy: "official_source_review",
        priority: 20,
        instructions: "Metni kopyalayıp resmî destek sayfasındaki uygun talep yolunu seçin.",
      }),
      createEscalationChannel({
        id: "d-smart-edevlet-cancellation",
        value: "https://www.turkiye.gov.tr/d-smart-abonelik-fesih-basvurusu",
        label: "e-Devlet Başvurusuna Git",
        sourceTitle: "D-Smart Abonelik Fesih Başvurusu",
        verifiedAt: "2026-07-17",
        priority: 10,
      }),
    ],
  },
  {
    companyId: "turkcell-superonline",
    companyName: "Turkcell Superonline",
    aliases: ["Turkcell Superonline", "Superonline"],
    subscriptionLabel: "Abonelik numarası",
    officialDomains: ["superonline.net", "turkiye.gov.tr"],
    issueTypes: CANCELLATION,
    notes: "Fesih e-postası yalnızca abonelik iptalinde kullanılabilir.",
    channels: [
      createChannel({
        id: "superonline-cancellation-email",
        type: CHANNEL_TYPES.EMAIL,
        value: "abonelikfesih@superonline.net",
        label: "Doğrulanmış abonelik fesih e-postası",
        purpose: "direct_cancellation_email",
        sourceUrl: "https://www.superonline.net/yardim/musteri-islemleri/fesih-islemleri/e-posta-yolu-ile-abonelik-feshi",
        sourceTitle: "Superonline E-Posta Yolu ile Abonelik Feshi",
        officialDomain: "superonline.net",
        verifiedAt: "2026-07-20",
        verifiedBy: "official_source_review",
        priority: 20,
        instructions: "Hazırlanan başvuru metnini e-posta ekranında kontrol edip gönderin.",
        requirements: [
          { code: "applicant_name_or_company_title", label: "Ad soyadınızı veya şirket ünvanını başvuruda belirtin.", severity: "required", appliesTo: ["iptal"] },
          { code: "customer_number_required", label: "Müşteri numaranızı başvuruda belirtin.", severity: "required", appliesTo: ["iptal"] },
          { code: "contact_number_required", label: "İrtibat numaranızı başvuruda belirtin.", severity: "required", appliesTo: ["iptal"] },
        ],
      }),
      createEscalationChannel({
        id: "superonline-edevlet-cancellation",
        value: "https://www.turkiye.gov.tr/superonline-abonelik-fesih-basvurusu",
        label: "e-Devlet Başvurusuna Git",
        sourceTitle: "Superonline Abonelik Fesih Başvurusu",
        verifiedAt: "2026-07-17",
        priority: 10,
      }),
    ],
  },
  {
    companyId: "vodafone-net",
    companyName: "Vodafone Net",
    aliases: ["Vodafone Net", "Vodafone Ev İnterneti"],
    subscriptionLabel: "Abonelik numarası",
    officialDomains: ["vodafone.com.tr", "turkiye.gov.tr"],
    issueTypes: ["iptal", "fatura", "iade"],
    notes: "Resmî talep formları genel talepler içindir; doğrudan fesih formu olarak etiketlenmez.",
    channels: [
      createChannel({
        id: "vodafone-net-official-request-forms",
        type: CHANNEL_TYPES.OFFICIAL_FORM,
        value: "https://www.vodafone.com.tr/yardim/tuketici-sikayetleri",
        label: "Vodafone Evde İnternet resmî talep formları",
        issueTypes: ["iptal", "fatura", "iade"],
        purpose: "general_request",
        sourceUrl: "https://www.vodafone.com.tr/yardim/tuketici-sikayetleri",
        sourceTitle: "Vodafone Tüketici Şikayetleri ve Evde İnternet Talep Formları",
        officialDomain: "vodafone.com.tr",
        verifiedAt: "2026-07-20",
        verifiedBy: "official_source_review",
        priority: 20,
        instructions: "Metni kopyalayıp Evde İnternet için uygun talep formunu seçin.",
      }),
      createEscalationChannel({
        id: "vodafone-net-edevlet-cancellation",
        value: "https://www.turkiye.gov.tr/vodafone-net-abonelik-fesih-basvurusu",
        label: "e-Devlet Başvurusuna Git",
        sourceTitle: "Vodafone Net Abonelik Fesih Başvurusu",
        verifiedAt: "2026-07-17",
        priority: 10,
      }),
    ],
  },
  {
    companyId: "turk-telekom",
    companyName: "Türk Telekom",
    aliases: ["Türk Telekom", "Türk Telekom Evde İnternet", "TTNET", "Tivibu"],
    subscriptionLabel: "Abonelik numarası",
    officialDomains: ["turktelekom.com.tr", "turkiye.gov.tr"],
    issueTypes: CANCELLATION,
    notes: "Fesih e-postası yalnızca ev interneti/Tivibu abonelik iptalinde kullanılabilir.",
    channels: [
      createChannel({
        id: "turk-telekom-cancellation-email",
        type: CHANNEL_TYPES.EMAIL,
        value: "internettivibuiptal@turktelekom.com.tr",
        label: "Doğrulanmış Evde İnternet / Tivibu iptal e-postası",
        purpose: "direct_cancellation_email",
        sourceUrl: "https://www.turktelekom.com.tr/yardim/bireysel-evde-internet/internet-paket-bilgileri",
        sourceTitle: "Türk Telekom Fesih İşlemleri Hakkında",
        officialDomain: "turktelekom.com.tr",
        verifiedAt: "2026-07-20",
        verifiedBy: "official_source_review",
        priority: 20,
        acceptsAttachments: true,
        instructions: "Hazırlanan başvuru metnini e-posta ekranında kontrol edip gönderin.",
        serviceScopes: ["evde-internet", "tivibu"],
        requirements: [
          { code: "registered_sender_email", label: "Bu e-postayı Türk Telekom’da kayıtlı adresinizden gönderin.", severity: "required", appliesTo: ["iptal"] },
          { code: "identity_document_attachment", label: "Kimlik fotokopinizi e-postaya ekleyin.", severity: "required", appliesTo: ["iptal"] },
          { code: "signed_petition_attachment", label: "İmzalı dilekçeyi e-postaya ekleyin.", severity: "required", appliesTo: ["iptal"] },
        ],
      }),
      createChannel({
        id: "turk-telekom-cancellation-form",
        type: CHANNEL_TYPES.OFFICIAL_FORM,
        value: "https://www.turktelekom.com.tr/tt-yardim/SozlesmeVeTaahhutnameler/TURK-TELEKOM-EVDE-%C4%B0NTERNET-%C4%B0PTAL-TALEP-FORMU.PDF",
        label: "Resmî İptal Formunu Aç",
        sourceUrl: "https://www.turktelekom.com.tr/tt-yardim/SozlesmeVeTaahhutnameler/TURK-TELEKOM-EVDE-%C4%B0NTERNET-%C4%B0PTAL-TALEP-FORMU.PDF",
        sourceTitle: "Türk Telekom Evde İnternet İptal Talep Formu",
        officialDomain: "turktelekom.com.tr",
        verifiedAt: "2026-07-17",
        priority: 30,
        instructions: "Resmî formu açmadan önce hazırlanan metni panoya kopyalayın.",
      }),
      createEscalationChannel({
        id: "turk-telekom-edevlet-cancellation",
        value: "https://www.turkiye.gov.tr/ttnet-abonelik-fesih-basvurusu",
        label: "e-Devlet Başvurusuna Git",
        sourceTitle: "TTNET Abonelik Fesih Başvurusu",
        verifiedAt: "2026-07-17",
        priority: 10,
      }),
    ],
  },
  {
    companyId: "digiturk",
    companyName: "Digiturk / beIN",
    aliases: ["Digiturk", "Digiturk / beIN", "Digiturk/beIN", "beIN"],
    subscriptionLabel: "Üyelik numarası",
    officialDomains: ["digiturk.com.tr", "turkiye.gov.tr"],
    issueTypes: CANCELLATION,
    notes: "İptal e-postası yalnız kayıtlı gönderici adresi ve gerekli eklerle kullanılabilir.",
    channels: [
      createChannel({
        id: "digiturk-cancellation-email",
        type: CHANNEL_TYPES.EMAIL,
        value: "abonelikiptal@digiturk.com.tr",
        label: "Doğrulanmış abonelik iptal e-postası",
        purpose: "direct_cancellation_email",
        sourceUrl: "https://destek.digiturk.com.tr/soru/uyelik_islemleri/uyelik_islemleri/digiturk-uyeligi-nasil-iptal-edilir",
        sourceTitle: "Digiturk Üyeliği Nasıl İptal Edilir?",
        officialDomain: "digiturk.com.tr",
        verifiedAt: "2026-07-20",
        verifiedBy: "official_source_review",
        priority: 10,
        acceptsAttachments: true,
        requirements: [
          { code: "registered_sender_email", label: "Bu e-postayı Digiturk üyeliğinizde kayıtlı adresinizden gönderin.", severity: "required", appliesTo: ["iptal"] },
          { code: "identity_document_attachment", label: "Kimlik belgenizi e-postaya ekleyin.", severity: "required", appliesTo: ["iptal"] },
          { code: "customer_number_required", label: "Üye veya müşteri numaranızı konuya ya da metne ekleyin.", severity: "required", appliesTo: ["iptal"] },
        ],
      }),
      createChannel({
        id: "digiturk-cancellation-info",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://destek.digiturk.com.tr/soru/uyelik_islemleri/uyelik_islemleri/digiturk-uyeligi-nasil-iptal-edilir",
        label: "Resmî İptal Bilgilerini Aç",
        sourceUrl: "https://destek.digiturk.com.tr/soru/uyelik_islemleri/uyelik_islemleri/digiturk-uyeligi-nasil-iptal-edilir",
        sourceTitle: "Digiturk Üyelik İptali Bilgileri",
        officialDomain: "digiturk.com.tr",
        verifiedAt: "2026-07-20",
        verifiedBy: "official_source_review",
        priority: 20,
        alternativeOnly: true,
        instructions: "Resmî sayfayı açmadan önce hazırlanan metni panoya kopyalayın.",
      }),
      createEscalationChannel({
        id: "digiturk-edevlet-cancellation",
        value: "https://www.turkiye.gov.tr/digiturk-abonelik-fesih-basvurusu",
        label: "e-Devlet Başvurusuna Git",
        sourceTitle: "Digiturk Abonelik Fesih Başvurusu",
        verifiedAt: "2026-07-17",
        priority: 10,
      }),
    ],
  },
  {
    companyId: "turknet",
    companyName: "TurkNet",
    aliases: ["TurkNet", "Turk.net"],
    subscriptionLabel: "Abonelik numarası",
    officialDomains: ["turk.net", "turkiye.gov.tr"],
    verifiedEmailDomains: ["turknet.net.tr"],
    issueTypes: CANCELLATION,
    notes: "E-posta ve Online İşlemler iptal için doğrulanmış alternatiflerdir.",
    channels: [
      createChannel({
        id: "turknet-cancellation-email",
        type: CHANNEL_TYPES.EMAIL,
        value: "iptal-faks@turknet.net.tr",
        label: "Doğrulanmış abonelik iptal e-postası",
        purpose: "direct_cancellation_email",
        sourceUrl: "https://www.turk.net/iptal",
        sourceTitle: "TurkNet Zahmetsiz İptal",
        officialDomain: "turk.net",
        verifiedAt: "2026-07-20",
        verifiedBy: "official_source_review",
        priority: 10,
        acceptsAttachments: true,
        requirements: [
          { code: "registered_sender_email", label: "Bu e-postayı TurkNet’te kayıtlı adresinizden gönderin.", severity: "required", appliesTo: ["iptal"] },
          { code: "subscriber_number_in_subject", label: "Konu kısmına abone numaranızı yazın.", severity: "required", appliesTo: ["iptal"] },
          { code: "identity_front_back_jpg", label: "Kimliğinizin ön ve arka yüzünü JPG olarak ekleyin.", severity: "required", appliesTo: ["iptal"] },
          { code: "documents_as_attachments", label: "Belgeleri e-posta gövdesine değil, dosya eki olarak ekleyin.", severity: "required", appliesTo: ["iptal"] },
          { code: "max_attachment_size_10mb", label: "Eklerin toplam boyutunu 10 MB’ın altında tutun.", severity: "required", appliesTo: ["iptal"] },
        ],
      }),
      createChannel({
        id: "turknet-online-cancellation",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://www.turk.net/online-islemler",
        label: "TurkNet Online İşlemler’den iptal",
        sourceUrl: "https://www.turk.net/iptal",
        sourceTitle: "TurkNet Online İşlemler Merkezi ile İptal",
        officialDomain: "turk.net",
        verifiedAt: "2026-07-20",
        verifiedBy: "official_source_review",
        priority: 20,
        instructions: "Online İşlemler hesabınızda hazırlanan başvuruyu kontrol ederek iletin.",
      }),
      createEscalationChannel({
        id: "turknet-edevlet-cancellation",
        value: "https://www.turkiye.gov.tr/turknet-abonelik-fesih-basvurusu",
        label: "e-Devlet Başvurusuna Git",
        sourceTitle: "TurkNet Abonelik Fesih Başvurusu",
        verifiedAt: "2026-07-17",
        priority: 20,
      }),
    ],
  },
  {
    companyId: "millenicom",
    companyName: "Millenicom",
    aliases: ["Millenicom", "Milleni"],
    subscriptionLabel: "Abonelik numarası",
    officialDomains: ["milleni.com.tr", "turkiye.gov.tr"],
    issueTypes: CANCELLATION,
    notes: "Genel görünümlü adres resmî iptal sayfasında açıkça iptal kanalı olarak yayımlanmıştır.",
    channels: [
      createChannel({
        id: "millenicom-cancellation-email",
        type: CHANNEL_TYPES.EMAIL,
        value: "info@milleni.com.tr",
        label: "Millenicom’un resmî e-posta ile iptal kanalı",
        purpose: "direct_cancellation_email",
        sourceUrl: "https://www.milleni.com.tr/eviniz-icin/destek/aboneligim-hakkinda/iptal",
        sourceTitle: "Millenicom Abonelik İptali",
        officialDomain: "milleni.com.tr",
        verifiedAt: "2026-07-20",
        verifiedBy: "official_source_review",
        priority: 20,
      }),
      createEscalationChannel({
        id: "millenicom-edevlet-cancellation",
        value: "https://www.turkiye.gov.tr/millenicom-abonelik-fesih-basvurusu",
        label: "e-Devlet Başvurusuna Git",
        sourceTitle: "Millenicom Abonelik Fesih Başvurusu",
        verifiedAt: "2026-07-17",
        priority: 10,
      }),
    ],
  },
  {
    companyId: "turksat-kablo",
    companyName: "Türksat Kablo",
    aliases: ["Türksat Kablo", "KabloNet", "Kablo TV"],
    serviceScopes: ["kablonet", "kablo-tv"],
    subscriptionLabel: "Abone numarası",
    officialDomains: ["turksatkablo.com.tr", "turkiye.gov.tr"],
    issueTypes: CANCELLATION,
    notes: "KabloNet ve Kablo TV iptali için resmî yönergeyi izleyin; imzalı dilekçe ve cihaz iadesi gerekebilir.",
    channels: [
      createChannel({
        id: "turksat-kablo-cancellation-instructions",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.turksatkablo.com.tr/internet-SSS%2C27",
        label: "Türksat Kablo resmî abonelik iptal yolları",
        purpose: "cancellation_instructions",
        sourceUrl: "https://www.turksatkablo.com.tr/internet-SSS%2C27",
        sourceTitle: "Türksat Kablo İnternet Sıkça Sorulan Sorular",
        officialDomain: "turksatkablo.com.tr",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        priority: 10,
        serviceScopes: ["kablonet", "kablo-tv"],
        instructions: "Hazırlanan metni kopyalayıp resmî iptal yönergesini açın; gerekli formu ve cihaz iadesi adımlarını siz tamamlayın.",
        requirements: [
          { code: "signed_petition_attachment", label: "İmzalı iptal formu/dilekçesi gerekebilir.", severity: "required", appliesTo: ["iptal"] },
          { code: "device_return_may_apply", label: "İptal sonrasında modem veya cihaz iadesi gerekebilir.", severity: "warning", appliesTo: ["iptal"] },
        ],
      }),
      createChannel({
        id: "turksat-kablo-call-center",
        type: CHANNEL_TYPES.PHONE,
        value: "0850 804 4444",
        label: "Türksat Kablo çağrı merkezi",
        purpose: "cancellation_support",
        sourceUrl: "https://www.turksatkablo.com.tr/internet-SSS%2C27",
        sourceTitle: "Türksat Kablo İnternet Sıkça Sorulan Sorular",
        officialDomain: "turksatkablo.com.tr",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        priority: 20,
        serviceScopes: ["kablonet", "kablo-tv"],
        showOnResult: true,
      }),
      createEscalationChannel({
        id: "turksat-kablo-edevlet-cancellation",
        value: "https://www.turkiye.gov.tr/btk-abonelik-fesih-basvurusu",
        label: "e-Devlet Abonelik Fesih Başvurusuna Git",
        sourceTitle: "BTK Abonelik Fesih Başvurusu",
        verifiedAt: "2026-07-17",
        priority: 30,
        serviceScopes: ["kablonet", "kablo-tv"],
        showOnResult: true,
      }),
    ],
  },
  {
    companyId: "turkcell-mobile",
    companyName: "Turkcell",
    aliases: ["Turkcell", "Turkcell Mobil"],
    serviceScopes: ["mobile"],
    subscriptionLabel: "Mobil hat numarası",
    officialDomains: ["turkcell.com.tr", "turkiye.gov.tr"],
    issueTypes: CANCELLATION,
    notes: "E-postayı kayıtlı adresinizden gönderin; Turkcell uygulaması, 532 ve e-Devlet alternatiflerini de kullanabilirsiniz.",
    channels: [
      createChannel({
        id: "turkcell-mobile-cancellation-email",
        type: CHANNEL_TYPES.EMAIL,
        value: "hatiptal@turkcell.com.tr",
        label: "Doğrulanmış mobil hat iptal e-postası",
        purpose: "direct_cancellation_email",
        sourceUrl: "https://www.turkcell.com.tr/destek/hattiniz/abonelik-islemleri/hattimi-nasil-iptal-ettirebilirim",
        sourceTitle: "Turkcell Hattımı Nasıl İptal Ettirebilirim?",
        officialDomain: "turkcell.com.tr",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        priority: 10,
        acceptsAttachments: true,
        serviceScopes: ["mobile"],
        instructions: "Hazırlanan metni Turkcell'de kayıtlı e-posta adresinizden kontrol ederek gönderin.",
        requirements: [
          { code: "registered_sender_email", label: "E-postayı Turkcell’de kayıtlı adresinizden gönderin.", severity: "required", appliesTo: ["iptal"] },
          { code: "identity_document_attachment", label: "Kimlik görselinizi e-postaya ekleyin.", severity: "required", appliesTo: ["iptal"] },
          { code: "mobile_line_number_required", label: "İptal edilmesini istediğiniz telefon numarasını metinde belirtin.", severity: "required", appliesTo: ["iptal"] },
        ],
      }),
      createChannel({
        id: "turkcell-mobile-cancellation-instructions",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.turkcell.com.tr/destek/hattiniz/abonelik-islemleri/hattimi-nasil-iptal-ettirebilirim",
        label: "Turkcell uygulaması ve diğer resmî iptal yolları",
        purpose: "cancellation_instructions",
        sourceUrl: "https://www.turkcell.com.tr/destek/hattiniz/abonelik-islemleri/hattimi-nasil-iptal-ettirebilirim",
        sourceTitle: "Turkcell Hattımı Nasıl İptal Ettirebilirim?",
        officialDomain: "turkcell.com.tr",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        priority: 20,
        serviceScopes: ["mobile"],
        instructions: "Turkcell uygulamasında Yardım > Evraklı Talepler > Hat İptali yolunu izleyebilirsiniz.",
      }),
      createChannel({
        id: "turkcell-mobile-customer-service",
        type: CHANNEL_TYPES.PHONE,
        value: "532",
        label: "Turkcell Müşteri Hizmetleri",
        purpose: "cancellation_support",
        sourceUrl: "https://www.turkcell.com.tr/destek/hattiniz/abonelik-islemleri/hattimi-nasil-iptal-ettirebilirim",
        sourceTitle: "Turkcell Hattımı Nasıl İptal Ettirebilirim?",
        officialDomain: "turkcell.com.tr",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        priority: 30,
        serviceScopes: ["mobile"],
        showOnResult: true,
      }),
      createEscalationChannel({
        id: "turkcell-mobile-edevlet-cancellation",
        value: "https://www.turkiye.gov.tr/btk-abonelik-fesih-basvurusu",
        label: "e-Devlet Abonelik Fesih Başvurusuna Git",
        sourceTitle: "BTK Abonelik Fesih Başvurusu",
        verifiedAt: "2026-07-17",
        priority: 40,
        serviceScopes: ["mobile"],
        showOnResult: true,
      }),
    ],
  },
  {
    companyId: "vodafone-mobile",
    companyName: "Vodafone",
    aliases: ["Vodafone", "Vodafone Mobil"],
    serviceScopes: ["mobile"],
    subscriptionLabel: "Mobil hat numarası",
    officialDomains: ["vodafone.com.tr", "turkiye.gov.tr"],
    issueTypes: CANCELLATION,
    notes: "Web hesabı, TOBi, müşteri hizmetleri, Vodafone mağazaları veya e-Devlet yolunu kullanın; doğrulanmış bir iptal e-postası yayımlanmamıştır.",
    channels: [
      createChannel({
        id: "vodafone-mobile-cancellation-instructions",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.vodafone.com.tr/yardim/ayarlar/hat-islemleri/hattimi-nasil-iptal-ettirebilirim",
        label: "Vodafone mobil hat resmî iptal yolları",
        purpose: "cancellation_instructions",
        sourceUrl: "https://www.vodafone.com.tr/yardim/ayarlar/hat-islemleri/hattimi-nasil-iptal-ettirebilirim",
        sourceTitle: "Vodafone Hattımı Nasıl İptal Ettirebilirim?",
        officialDomain: "vodafone.com.tr",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        priority: 10,
        serviceScopes: ["mobile"],
        instructions: "Metni kopyalayıp resmî iptal yolları sayfasını açın; işlemi siz tamamlayın.",
      }),
      createEscalationChannel({
        id: "vodafone-mobile-edevlet-cancellation",
        value: "https://www.turkiye.gov.tr/vodafone-abonelik-fesih-basvurusu",
        label: "e-Devlet Vodafone Abonelik Fesih Başvurusuna Git",
        sourceTitle: "Vodafone Abonelik Fesih Başvurusu",
        verifiedAt: "2026-07-21",
        priority: 20,
        serviceScopes: ["mobile"],
        showOnResult: true,
      }),
    ],
  },
  {
    companyId: "turk-telekom-mobile",
    companyName: "Türk Telekom Mobil",
    aliases: ["Türk Telekom Mobil", "Türk Telekom Mobile"],
    serviceScopes: ["mobile"],
    subscriptionLabel: "Mobil hat numarası",
    officialDomains: ["turktelekom.com.tr", "turkiye.gov.tr"],
    issueTypes: CANCELLATION,
    notes: "Online İşlemler kanalı yalnız iptal ön başvurusudur; kesin fesih tamamlandı anlamına gelmez. 444 1 444 ve e-Devlet alternatiflerini kullanabilirsiniz.",
    channels: [
      createChannel({
        id: "turk-telekom-mobile-cancellation-pre-application",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://www.turktelekom.com.tr/yardim/bireysel-mobil/faturali-hat-islemleri",
        label: "Türk Telekom Mobil iptal ön başvurusu",
        purpose: "cancellation_pre_application",
        sourceUrl: "https://www.turktelekom.com.tr/yardim/bireysel-mobil/faturali-hat-islemleri",
        sourceTitle: "Türk Telekom Faturalı Hat İşlemleri",
        officialDomain: "turktelekom.com.tr",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        priority: 10,
        serviceScopes: ["mobile"],
        instructions: "Online İşlemler'den yalnız iptal ön başvurusu yapılır; kesin fesih işlemini tamamladığınızı varsaymayın.",
      }),
      createChannel({
        id: "turk-telekom-mobile-customer-service",
        type: CHANNEL_TYPES.PHONE,
        value: "444 1 444",
        label: "Türk Telekom Müşteri Hizmetleri",
        purpose: "cancellation_support",
        sourceUrl: "https://www.turktelekom.com.tr/yardim/bireysel-mobil/faturali-hat-islemleri",
        sourceTitle: "Türk Telekom Faturalı Hat İşlemleri",
        officialDomain: "turktelekom.com.tr",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        priority: 20,
        serviceScopes: ["mobile"],
        showOnResult: true,
      }),
      createEscalationChannel({
        id: "turk-telekom-mobile-edevlet-cancellation",
        value: "https://www.turkiye.gov.tr/btk-abonelik-fesih-basvurusu",
        label: "e-Devlet Abonelik Fesih Başvurusuna Git",
        sourceTitle: "BTK Abonelik Fesih Başvurusu",
        verifiedAt: "2026-07-17",
        priority: 30,
        serviceScopes: ["mobile"],
        showOnResult: true,
      }),
    ],
  },
  {
    companyId: "netflix",
    companyName: "Netflix",
    aliases: ["Netflix"],
    category: "digital",
    serviceScopes: ["streaming"],
    subscriptionLabel: "Hesap e-posta adresi",
    officialDomains: ["netflix.com", "help.netflix.com"],
    issueTypes: ["iptal", "iade"],
    notes: "Abonelik İptal Netflix şifrenizi istemez. Doğrudan faturalandırılan üyeliklerde iptal mevcut fatura dönemi sonunda sonuçlanabilir; iade uygunluğu garanti edilmez.",
    channels: [
      createChannel({
        id: "netflix-direct-cancellation",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://www.netflix.com/cancelplan",
        actionUrl: "https://www.netflix.com/cancelplan",
        label: "Netflix üyeliğini yönet ve iptal et",
        purpose: "direct_cancellation",
        sourceUrl: "https://help.netflix.com/tr/node/407",
        sourceTitle: "Netflix Üyeliği Nasıl İptal Edilir?",
        officialDomain: "netflix.com",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        serviceScopes: ["streaming"],
        billingProviders: ["direct"],
        instructions: "Netflix hesap sayfasını açın; oturum açma ve iptal onayını siz tamamlayın.",
      }),
      createChannel({
        id: "netflix-refund-help",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://help.netflix.com/tr/",
        actionUrl: "https://help.netflix.com/tr/",
        label: "Netflix resmî yardım ve faturalama sayfası",
        purpose: "refund_instructions",
        issueTypes: ["iade"],
        sourceUrl: "https://help.netflix.com/tr/",
        sourceTitle: "Netflix Yardım Merkezi",
        officialDomain: "help.netflix.com",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        serviceScopes: ["streaming"],
        billingProviders: ["direct"],
        instructions: "Resmî yardım sayfasını inceleyin; iade hakkı veya sonucu garanti edilmez.",
      }),
    ],
  },
  {
    companyId: "exxen",
    companyName: "Exxen",
    aliases: ["Exxen"],
    category: "digital",
    serviceScopes: ["streaming"],
    subscriptionLabel: "Hesap e-posta adresi",
    officialDomains: ["exxen.com", "plan.exxen.com"],
    issueTypes: CANCELLATION,
    notes: "Abonelik Yönetimi bağlantısı Exxen'in resmî plan sayfasından doğrulandı. Abonelik İptal hesap şifrenizi istemez.",
    channels: [createChannel({
      id: "exxen-subscription-management",
      type: CHANNEL_TYPES.ONLINE_ACCOUNT,
      value: "https://plan.exxen.com/tr/otp/email",
      actionUrl: "https://plan.exxen.com/tr/otp/email",
      label: "Exxen Abonelik Yönetimi",
      purpose: "subscription_management",
      sourceUrl: "https://plan.exxen.com/",
      sourceTitle: "Exxen Abonelik Yönetimi",
      officialDomain: "plan.exxen.com",
      verifiedAt: "2026-07-21",
      verifiedBy: "official_source_review",
      serviceScopes: ["streaming"],
      billingProviders: ["direct"],
      instructions: "Exxen'in resmî hesap doğrulama ekranını açın; hesabınıza kendiniz giriş yapın.",
    })],
  },
  {
    companyId: "max",
    companyName: "Max",
    aliases: ["BluTV", "HBO Max", "Max"],
    category: "digital",
    serviceScopes: ["streaming"],
    subscriptionLabel: "Hesap e-posta adresi",
    officialDomains: ["max.com", "help.max.com"],
    issueTypes: ["iptal", "iade"],
    notes: "BluTV abonelikleri Max hesap ve faturalandırma sistemine taşındı. İşlemi ücreti tahsil eden sağlayıcının kanalında tamamlayın.",
    channels: [
      createChannel({
        id: "max-subscription-management",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://help.max.com/tr-tr/Answer/Detail/000002526",
        actionUrl: "https://help.max.com/tr-tr/Answer/Detail/000002526",
        label: "Max aboneliğini yönet",
        purpose: "subscription_management",
        sourceUrl: "https://help.max.com/tr-tr/Answer/Detail/000002526",
        sourceTitle: "Max Aboneliğini Değiştirme veya İptal Etme",
        officialDomain: "help.max.com",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        serviceScopes: ["streaming"],
        billingProviders: ["direct"],
      }),
      createChannel({
        id: "max-refund-instructions",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://help.max.com/tr-tr/answer/detail/000002522",
        actionUrl: "https://help.max.com/tr-tr/answer/detail/000002522",
        label: "Max para iadesi resmî açıklaması",
        purpose: "refund_instructions",
        issueTypes: ["iade"],
        sourceUrl: "https://help.max.com/tr-tr/answer/detail/000002522",
        sourceTitle: "Max Para İadeleri",
        officialDomain: "help.max.com",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        serviceScopes: ["streaming"],
        billingProviders: ["direct"],
        instructions: "İade uygunluğu garanti edilmez; faturalandırma sağlayıcınıza göre resmî açıklamayı izleyin.",
      }),
    ],
  },
  {
    companyId: "tod",
    companyName: "TOD",
    aliases: ["TOD", "TOD TV"],
    category: "digital",
    serviceScopes: ["streaming"],
    subscriptionLabel: "Hesap e-posta adresi",
    officialDomains: ["todtv.com.tr", "destek.todtv.com.tr"],
    issueTypes: CANCELLATION,
    notes: "Doğrulanmış iptal veya üyelik yönetimi action URL'si bulunamadı; tahminî adres önerilmiyor.",
    channels: [createChannel({
      id: "tod-subscription-management-review",
      type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
      value: "https://www.todtv.com.tr/",
      label: "TOD resmî ana sayfası",
      purpose: "subscription_management_review",
      sourceUrl: "https://www.todtv.com.tr/",
      sourceTitle: "TOD Resmî Sitesi",
      officialDomain: "todtv.com.tr",
      verifiedAt: "2026-07-21",
      verifiedBy: "official_source_review",
      status: CHANNEL_STATUSES.PENDING_REVIEW,
      reviewReason: "Doğrulanmış üyelik yönetimi veya iptal action URL'si bulunamadı.",
      serviceScopes: ["streaming"],
      billingProviders: ["direct"],
    })],
  },
  {
    companyId: "youtube-premium",
    companyName: "YouTube Premium",
    aliases: ["YouTube Premium"],
    category: "digital",
    serviceScopes: ["premium-membership"],
    subscriptionLabel: "Google hesap e-posta adresi",
    officialDomains: ["youtube.com", "support.google.com", "google.com"],
    issueTypes: ["iptal", "iade"],
    notes: "Google Play veya Apple tarafından faturalandırılan üyeliklerde işlemi ilgili uygulama mağazası kanalında tamamlayın.",
    channels: [
      createChannel({
        id: "youtube-premium-membership-management",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://www.youtube.com/paid_memberships",
        actionUrl: "https://www.youtube.com/paid_memberships",
        label: "YouTube ücretli üyeliklerini yönet",
        purpose: "direct_cancellation",
        sourceUrl: "https://support.google.com/youtube/answer/6308278",
        sourceTitle: "YouTube Premium Üyeliğini İptal Etme",
        officialDomain: "youtube.com",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        serviceScopes: ["premium-membership"],
        billingProviders: ["direct"],
      }),
      createChannel({
        id: "youtube-premium-refund-instructions",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://support.google.com/youtube/answer/12014038",
        actionUrl: "https://support.google.com/youtube/answer/12014038",
        label: "YouTube Premium para iadesi yardımı",
        purpose: "refund_instructions",
        issueTypes: ["iade"],
        sourceUrl: "https://support.google.com/youtube/answer/12014038",
        sourceTitle: "YouTube Premium ve Music Premium Para İadeleri",
        officialDomain: "support.google.com",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        serviceScopes: ["premium-membership"],
        billingProviders: ["direct"],
      }),
    ],
  },
  {
    companyId: "spotify-premium",
    companyName: "Spotify Premium",
    aliases: ["Spotify", "Spotify Premium"],
    category: "digital",
    serviceScopes: ["music"],
    subscriptionLabel: "Hesap e-posta adresi",
    officialDomains: ["spotify.com", "support.spotify.com"],
    issueTypes: CANCELLATION,
    notes: "Hesabınızda bir iş ortağı görünüyorsa iptal işlemini o sağlayıcıyla tamamlayın. Doğrulanmış iptal e-postası yoktur.",
    channels: [createChannel({
      id: "spotify-premium-plan-management",
      type: CHANNEL_TYPES.ONLINE_ACCOUNT,
      value: "https://www.spotify.com/account/",
      actionUrl: "https://www.spotify.com/account/",
      label: "Spotify Premium planını yönet",
      purpose: "direct_cancellation",
      sourceUrl: "https://support.spotify.com/tr-tr/article/cancel-premium/",
      sourceTitle: "Spotify Premium'u İptal Etme",
      officialDomain: "spotify.com",
      verifiedAt: "2026-07-21",
      verifiedBy: "official_source_review",
      serviceScopes: ["music"],
      billingProviders: ["direct"],
    })],
  },
  {
    companyId: "google-play",
    companyName: "Google Play",
    aliases: ["Google Play"],
    category: "digital",
    serviceScopes: ["app-marketplace"],
    subscriptionLabel: "Google hesap e-posta adresi",
    officialDomains: ["play.google.com", "support.google.com", "google.com"],
    issueTypes: ["iptal", "iade"],
    notes: "Google hesabınıza resmî sayfada kendiniz giriş yapın. İptal ve para iadesi ayrı işlemlerdir; iade uygunluğu garanti edilmez.",
    channels: [
      createChannel({
        id: "google-play-subscription-management",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://play.google.com/store/account/subscriptions",
        actionUrl: "https://play.google.com/store/account/subscriptions",
        label: "Google Play aboneliklerini yönet",
        purpose: "subscription_management",
        sourceUrl: "https://support.google.com/googleplay/answer/7018481?hl=tr",
        sourceTitle: "Google Play Aboneliklerini İptal Etme",
        officialDomain: "play.google.com",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        serviceScopes: ["app-marketplace"],
        billingProviders: ["direct", "google_play"],
      }),
      createChannel({
        id: "google-play-refund-request",
        type: CHANNEL_TYPES.OFFICIAL_FORM,
        value: "https://support.google.com/googleplay/workflow/9813244",
        actionUrl: "https://support.google.com/googleplay/workflow/9813244",
        label: "Google Play para iadesi talep et",
        purpose: "refund_request",
        issueTypes: ["iade"],
        sourceUrl: "https://support.google.com/googleplay/answer/15574897",
        sourceTitle: "Google Play Para İadesi İsteme",
        officialDomain: "support.google.com",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        serviceScopes: ["app-marketplace"],
        billingProviders: ["direct", "google_play"],
        instructions: "Resmî formu açın ve talebi kendiniz gönderin; iade onayı garanti edilmez.",
      }),
    ],
  },
  {
    companyId: "apple-app-store",
    companyName: "Apple App Store",
    aliases: ["Apple", "Apple App Store", "App Store"],
    category: "digital",
    serviceScopes: ["app-marketplace"],
    subscriptionLabel: "Apple hesabı e-posta adresi",
    officialDomains: ["apple.com", "support.apple.com", "account.apple.com", "reportaproblem.apple.com"],
    issueTypes: ["iptal", "iade"],
    notes: "Abonelik İptal Apple hesabınızı veya şifrenizi istemez. Oturum açma ve talep gönderme işlemlerini Apple sayfasında siz tamamlarsınız.",
    channels: [
      createChannel({
        id: "apple-subscription-management",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://account.apple.com/",
        actionUrl: "https://account.apple.com/",
        label: "Apple aboneliklerini yönet",
        purpose: "subscription_management",
        sourceUrl: "https://support.apple.com/tr-tr/118428",
        sourceTitle: "Apple Aboneliğini İptal Etme",
        officialDomain: "account.apple.com",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        serviceScopes: ["app-marketplace"],
        billingProviders: ["direct", "apple"],
      }),
      createChannel({
        id: "apple-refund-request",
        type: CHANNEL_TYPES.OFFICIAL_FORM,
        value: "https://reportaproblem.apple.com/",
        actionUrl: "https://reportaproblem.apple.com/",
        label: "Apple para iadesi talep et",
        purpose: "refund_request",
        issueTypes: ["iade"],
        sourceUrl: "https://support.apple.com/tr-tr/118223",
        sourceTitle: "Apple Para İadesi Talep Etme",
        officialDomain: "reportaproblem.apple.com",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        serviceScopes: ["app-marketplace"],
        billingProviders: ["direct", "apple"],
        instructions: "Resmî Apple sayfasında oturum açıp talebi kendiniz gönderin; iade sonucu garanti edilmez.",
      }),
    ],
  },
  {
    companyId: "amazon-prime",
    companyName: "Amazon Prime",
    aliases: ["Amazon Prime", "Prime"],
    category: "digital",
    serviceScopes: ["membership"],
    subscriptionLabel: "Amazon hesap e-posta adresi",
    officialDomains: ["amazon.com.tr"],
    issueTypes: CANCELLATION,
    notes: "Doğrulanmış ayrı bir üyelik yönetimi action URL'si bulunamadı. Amazon hesabınızdaki Prime üyelik yönetimine resmî Prime sayfasından gidin.",
    channels: [
      createChannel({
        id: "amazon-prime-management-review",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://www.amazon.com.tr/prime",
        label: "Amazon Prime üyeliğini yönet",
        purpose: "subscription_management",
        sourceUrl: "https://www.amazon.com.tr/prime",
        sourceTitle: "Amazon Prime",
        officialDomain: "amazon.com.tr",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        status: CHANNEL_STATUSES.PENDING_REVIEW,
        reviewReason: "Ayrı üyelik yönetimi action URL'si doğrulanamadı.",
        serviceScopes: ["membership"],
        billingProviders: ["direct"],
      }),
      createChannel({
        id: "amazon-prime-official-page",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.amazon.com.tr/prime",
        actionUrl: "https://www.amazon.com.tr/prime",
        label: "Amazon Prime resmî sayfasını aç",
        purpose: "membership_information",
        sourceUrl: "https://www.amazon.com.tr/prime",
        sourceTitle: "Amazon Prime",
        officialDomain: "amazon.com.tr",
        verifiedAt: "2026-07-21",
        verifiedBy: "official_source_review",
        serviceScopes: ["membership"],
        billingProviders: ["direct"],
        instructions: "Resmî Prime sayfasından hesabınızdaki üyelik yönetimine gidin; tahminî bir hesap URL'si kullanılmıyor.",
      }),
    ],
  },
  {
    companyId: "trendyol",
    companyName: "Trendyol",
    aliases: ["Trendyol"],
    category: "ecommerce",
    serviceScopes: ["marketplace"],
    subscriptionLabel: "Sipariş numarası",
    officialDomains: ["trendyol.com"],
    issueTypes: ["iade", "fatura"],
    notes: "İade ve sipariş sorunlarında önce Hesabım > Siparişlerim > Sipariş Detayı yolunu kullanın. KEP adresi tüketici iade e-postası değildir.",
    channels: [
      createChannel({
        id: "trendyol-orders-return",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://www.trendyol.com/Hesabim/Siparislerim",
        actionUrl: "https://www.trendyol.com/Hesabim/Siparislerim",
        label: "Trendyol Siparişlerim’den iade talebi oluştur",
        purpose: "return_request",
        issueTypes: ["iade"],
        sourceUrl: "https://www.trendyol.com/yardim/iade",
        sourceTitle: "Trendyol İade Yardımı",
        officialDomain: "trendyol.com",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        commerceProblems: ["cancel_before_shipping", "return_product", "defective_or_wrong_product", "refund_not_received", "return_rejected", "not_delivered", "duplicate_charge", "other"],
        instructions: "Siparişlerim > Sipariş Detayı > İade seçeneği adımlarını izleyin ve gösterilen iade yöntemi veya kodunu kullanın.",
      }),
      createChannel({
        id: "trendyol-general-support",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.trendyol.com/yardim",
        actionUrl: "https://www.trendyol.com/yardim",
        label: "Trendyol Yardım ve Canlı Destek",
        purpose: "general_support",
        issueTypes: ["iade", "fatura"],
        sourceUrl: "https://www.trendyol.com/yardim",
        sourceTitle: "Trendyol Yardım",
        officialDomain: "trendyol.com",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        commerceProblems: ["unreachable"],
        priority: 20,
      }),
    ],
  },
  {
    companyId: "hepsiburada",
    companyName: "Hepsiburada",
    aliases: ["Hepsiburada"],
    category: "ecommerce",
    serviceScopes: ["marketplace"],
    subscriptionLabel: "Sipariş numarası",
    officialDomains: ["hepsiburada.com"],
    issueTypes: ["iade", "fatura"],
    notes: "Doğrulanmış tüketici iade e-postası yoktur; Çözüm Merkezi ana işlem kanalıdır.",
    channels: [
      createChannel({
        id: "hepsiburada-resolution-center-return",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://www.hepsiburada.com/cozummerkezi",
        actionUrl: "https://www.hepsiburada.com/cozummerkezi",
        label: "Hepsiburada Çözüm Merkezi’nden iade talebi oluştur",
        purpose: "return_request",
        issueTypes: ["iade"],
        sourceUrl: "https://www.hepsiburada.com/kolay-iade",
        sourceTitle: "Hepsiburada Kolay İade",
        officialDomain: "hepsiburada.com",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        commerceProblems: ["cancel_before_shipping", "return_product", "defective_or_wrong_product", "refund_not_received", "return_rejected", "not_delivered", "duplicate_charge", "other"],
      }),
      createChannel({
        id: "hepsiburada-general-support",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.hepsiburada.com/alisveris-hakkinda",
        actionUrl: "https://www.hepsiburada.com/alisveris-hakkinda",
        label: "Hepsiburada alışveriş yardımı",
        purpose: "general_support",
        issueTypes: ["iade", "fatura"],
        sourceUrl: "https://www.hepsiburada.com/alisveris-hakkinda",
        sourceTitle: "Hepsiburada Alışveriş Hakkında",
        officialDomain: "hepsiburada.com",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        commerceProblems: ["unreachable"],
      }),
    ],
  },
  {
    companyId: "amazon-turkiye",
    companyName: "Amazon Türkiye",
    aliases: ["Amazon Türkiye", "Amazon.com.tr"],
    category: "ecommerce",
    serviceScopes: ["marketplace"],
    subscriptionLabel: "Sipariş numarası",
    officialDomains: ["amazon.com.tr"],
    issueTypes: ["iade", "fatura"],
    notes: "Ayrı Siparişlerim action URL’si doğrulanamadığı için tahminî hesap adresi kullanılmaz. Üçüncü taraf satıcı varsa mağaza adını başvuruya ekleyin.",
    channels: [
      createChannel({
        id: "amazon-turkiye-return-instructions",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.amazon.com.tr/gp/help/customer/display.html?nodeId=G6E3B2E8QPHQ88KF",
        actionUrl: "https://www.amazon.com.tr/gp/help/customer/display.html?nodeId=G6E3B2E8QPHQ88KF",
        label: "Amazon Türkiye resmî ürün iade yönergesini aç",
        purpose: "return_instructions",
        issueTypes: ["iade"],
        sourceUrl: "https://www.amazon.com.tr/gp/help/customer/display.html?nodeId=G6E3B2E8QPHQ88KF",
        sourceTitle: "Amazon Türkiye Ürün İadeleri",
        officialDomain: "amazon.com.tr",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        commerceProblems: ["cancel_before_shipping", "return_product", "defective_or_wrong_product", "return_rejected", "not_delivered", "duplicate_charge", "other"],
        instructions: "Resmî yönergeden hesabınızdaki Siparişlerim bölümüne gidin; Abonelik İptal Amazon şifrenizi istemez.",
      }),
      createChannel({
        id: "amazon-turkiye-refund-status",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.amazon.com.tr/gp/help/customer/display.html?nodeId=GKQNFKFK5CF3C54B",
        actionUrl: "https://www.amazon.com.tr/gp/help/customer/display.html?nodeId=GKQNFKFK5CF3C54B",
        label: "Amazon Türkiye para iadesi durum bilgisini aç",
        purpose: "refund_status_information",
        issueTypes: ["iade"],
        sourceUrl: "https://www.amazon.com.tr/gp/help/customer/display.html?nodeId=GKQNFKFK5CF3C54B",
        sourceTitle: "Amazon Türkiye Para İadeleri",
        officialDomain: "amazon.com.tr",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        commerceProblems: ["refund_not_received"],
        priority: 5,
      }),
      createChannel({
        id: "amazon-turkiye-customer-service",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.amazon.com.tr/gp/help/customer/display.html?nodeId=GSD587LKW72HKU2V",
        actionUrl: "https://www.amazon.com.tr/gp/help/customer/display.html?nodeId=GSD587LKW72HKU2V",
        label: "Amazon Müşteri Hizmetleri",
        purpose: "general_support",
        issueTypes: ["iade", "fatura"],
        sourceUrl: "https://www.amazon.com.tr/gp/help/customer/display.html?nodeId=GSD587LKW72HKU2V",
        sourceTitle: "Amazon Türkiye Müşteri Hizmetleri",
        officialDomain: "amazon.com.tr",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        commerceProblems: ["unreachable"],
      }),
    ],
  },
  {
    companyId: "n11",
    companyName: "n11",
    aliases: ["n11", "N11"],
    category: "ecommerce",
    serviceScopes: ["marketplace"],
    subscriptionLabel: "Sipariş numarası",
    officialDomains: ["n11.com"],
    issueTypes: ["iade", "fatura"],
    notes: "Tüketici işlemleri n11 Destek Merkezi’nden yürütülür; mağaza destek sayfaları ana tüketici kanalı değildir.",
    channels: [createChannel({
      id: "n11-consumer-support-return",
      type: CHANNEL_TYPES.ONLINE_ACCOUNT,
      value: "https://www.n11.com/destek-merkezi",
      actionUrl: "https://www.n11.com/destek-merkezi",
      label: "n11 Destek Merkezi’nden iade talebi oluştur",
      purpose: "return_request",
      issueTypes: ["iade", "fatura"],
      sourceUrl: "https://www.n11.com/destek-merkezi",
      sourceTitle: "n11 Destek Merkezi",
      officialDomain: "n11.com",
      verifiedAt: "2026-07-22",
      verifiedBy: "official_source_review",
      serviceScopes: ["marketplace"],
      commerceProblems: ["cancel_before_shipping", "return_product", "defective_or_wrong_product", "refund_not_received", "return_rejected", "not_delivered", "duplicate_charge", "unreachable", "other"],
    })],
  },
  {
    companyId: "pttavm",
    companyName: "PttAVM",
    aliases: ["PttAVM", "PTT AVM"],
    category: "ecommerce",
    serviceScopes: ["marketplace"],
    subscriptionLabel: "Sipariş numarası",
    officialDomains: ["pttavm.com"],
    verifiedEmailDomains: ["pttavm.com"],
    issueTypes: ["iade"],
    notes: "Üyeli siparişlerde hesap içi Sipariş İade ana kanaldır. Doğrulanmış misafir destek e-postası yalnız üye olmadan verilen siparişlerin iadesinde kullanılabilir.",
    channels: [
      createChannel({
        id: "pttavm-member-order-return",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://www.pttavm.com/my/siparis-takip",
        actionUrl: "https://www.pttavm.com/my/siparis-takip",
        label: "PttAVM Sipariş Takip’ten iade talebi oluştur",
        purpose: "return_request",
        issueTypes: ["iade"],
        sourceUrl: "https://www.pttavm.com/static-content/urun-iade-proseduru",
        sourceTitle: "PttAVM Ürün İade Prosedürü",
        officialDomain: "pttavm.com",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        purchaseAccountModes: ["member"],
        commerceProblems: ["cancel_before_shipping", "return_product", "defective_or_wrong_product", "refund_not_received", "return_rejected", "not_delivered", "duplicate_charge", "unreachable", "other"],
        instructions: "Hesabım > Sipariş Takip > Sipariş İade adımlarını izleyin.",
      }),
      createChannel({
        id: "pttavm-guest-order-return-email",
        type: CHANNEL_TYPES.EMAIL,
        value: "destek@pttavm.com",
        label: "PttAVM misafir sipariş iade desteği",
        purpose: "guest_order_return_support",
        issueTypes: ["iade"],
        sourceUrl: "https://www.pttavm.com/static-content/urun-iade-proseduru",
        sourceTitle: "PttAVM Ürün İade Prosedürü",
        officialDomain: "pttavm.com",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        purchaseAccountModes: ["guest"],
        commerceProblems: ["cancel_before_shipping", "return_product", "defective_or_wrong_product", "refund_not_received", "return_rejected", "not_delivered", "duplicate_charge", "unreachable", "other"],
        acceptsAttachments: true,
      }),
      createChannel({
        id: "pttavm-return-procedure",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.pttavm.com/static-content/urun-iade-proseduru",
        actionUrl: "https://www.pttavm.com/static-content/urun-iade-proseduru",
        label: "PttAVM resmî ürün iade prosedürü",
        purpose: "return_instructions",
        issueTypes: ["iade"],
        sourceUrl: "https://www.pttavm.com/static-content/urun-iade-proseduru",
        sourceTitle: "PttAVM Ürün İade Prosedürü",
        officialDomain: "pttavm.com",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        purchaseAccountModes: ["unknown"],
        commerceProblems: ["cancel_before_shipping", "return_product", "defective_or_wrong_product", "refund_not_received", "return_rejected", "not_delivered", "duplicate_charge", "unreachable", "other"],
      }),
    ],
  },
  {
    companyId: "ciceksepeti",
    companyName: "ÇiçekSepeti",
    aliases: ["ÇiçekSepeti", "Çiçek Sepeti"],
    category: "ecommerce",
    serviceScopes: ["marketplace"],
    subscriptionLabel: "Sipariş numarası",
    officialDomains: ["ciceksepeti.com"],
    issueTypes: ["iade", "fatura"],
    notes: "İade sürecini Siparişlerim veya Canlı Yardım üzerinden başlatın. Ürünü ÇiçekSepeti ofis adresine kendiliğinizden göndermeyin.",
    channels: [
      createChannel({
        id: "ciceksepeti-easy-return",
        type: CHANNEL_TYPES.ONLINE_ACCOUNT,
        value: "https://www.ciceksepeti.com/s/kolay-iade",
        actionUrl: "https://www.ciceksepeti.com/s/kolay-iade",
        label: "ÇiçekSepeti Siparişlerim’den iade talebi oluştur",
        purpose: "return_request",
        issueTypes: ["iade"],
        sourceUrl: "https://www.ciceksepeti.com/s/destek-siparis-iptal-iade",
        sourceTitle: "ÇiçekSepeti Sipariş İptal ve İade",
        officialDomain: "ciceksepeti.com",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        commerceProblems: ["cancel_before_shipping", "return_product", "defective_or_wrong_product", "refund_not_received", "return_rejected", "not_delivered", "duplicate_charge", "other"],
      }),
      createChannel({
        id: "ciceksepeti-live-help",
        type: CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
        value: "https://www.ciceksepeti.com/s/bize-ulasin",
        actionUrl: "https://www.ciceksepeti.com/s/bize-ulasin",
        label: "ÇiçekSepeti Canlı Yardım",
        purpose: "general_support",
        issueTypes: ["iade", "fatura"],
        sourceUrl: "https://www.ciceksepeti.com/s/bize-ulasin",
        sourceTitle: "ÇiçekSepeti Bize Ulaşın",
        officialDomain: "ciceksepeti.com",
        verifiedAt: "2026-07-22",
        verifiedBy: "official_source_review",
        serviceScopes: ["marketplace"],
        commerceProblems: ["unreachable"],
      }),
    ],
  },
]);

export const VERIFIED_GLOBAL_CHANNELS = deepFreeze({
  consumerArbitration: createChannel({
    id: "consumer-arbitration-edevlet",
    type: CHANNEL_TYPES.ESCALATION,
    value: "https://www.turkiye.gov.tr/tuketici-sikayeti-uygulamasi",
    label: "Hakem Heyeti Başvurusuna Git",
    issueTypes: ["hakem"],
    sourceUrl: "https://www.turkiye.gov.tr/tuketici-sikayeti-uygulamasi",
    sourceTitle: "Tüketici Hakem Heyetlerine Başvuru",
    officialDomain: "turkiye.gov.tr",
    verifiedAt: "2026-07-17",
    priority: 1,
    alternativeOnly: true,
    instructions: "Bu kanal kurumun ilk başvuru kanalı değildir.",
  }),
  btkCancellation: createEscalationChannel({
    id: "btk-cancellation-edevlet",
    value: "https://www.turkiye.gov.tr/btk-abonelik-fesih-basvurusu",
    label: "BTK Abonelik Fesih Başvurusuna Git",
    sourceTitle: "BTK Abonelik Fesih Başvurusu",
    verifiedAt: "2026-07-17",
    priority: 100,
  }),
});

const DIRECT_TYPE_RANK = Object.freeze({
  [CHANNEL_TYPES.EMAIL]: 1,
  [CHANNEL_TYPES.OFFICIAL_FORM]: 2,
  [CHANNEL_TYPES.ONLINE_ACCOUNT]: 3,
  [CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE]: 4,
  [CHANNEL_TYPES.PHONE]: 5,
  [CHANNEL_TYPES.POSTAL]: 5,
});

const PRIMARY_CHANNEL_TYPES = new Set([
  CHANNEL_TYPES.EMAIL,
  CHANNEL_TYPES.OFFICIAL_FORM,
  CHANNEL_TYPES.ONLINE_ACCOUNT,
  CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
]);

function isIsoDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value || "")
    && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
}

function normalizeDomain(value) {
  return String(value || "").trim().toLowerCase().replace(/^\.+|\.+$/g, "");
}

export function isSafeHttpsUrl(value) {
  if (typeof value !== "string") return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

export function isOfficialDomainMatch(hostname, officialDomain) {
  const host = normalizeDomain(hostname);
  const domain = normalizeDomain(officialDomain);
  return Boolean(domain && (host === domain || host.endsWith(`.${domain}`)));
}

export function isOfficialDomainUrl(url, officialDomains = []) {
  if (!isSafeHttpsUrl(url)) return false;
  const hostname = new URL(url).hostname;
  return officialDomains.some((domain) => isOfficialDomainMatch(hostname, domain));
}

function isRegisteredOfficialDomain(domain, institution) {
  const normalized = normalizeDomain(domain);
  return (institution?.officialDomains || [])
    .some((candidate) => normalizeDomain(candidate) === normalized);
}

export function isVerifiedInstitution(institution) {
  return Boolean(
    institution?.companyId
    && institution.companyName
    && Array.isArray(institution.officialDomains)
    && institution.officialDomains.length > 0
    && Array.isArray(institution.issueTypes)
    && Array.isArray(institution.channels)
  );
}

function isChannelValueValid(channel, institution) {
  if (!channel?.value || !isRegisteredOfficialDomain(channel.officialDomain, institution)) return false;
  if (channel.type === CHANNEL_TYPES.EMAIL) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(channel.value)) return false;
    const emailDomain = channel.value.split("@")[1];
    return isOfficialDomainMatch(emailDomain, channel.officialDomain)
      || (institution.verifiedEmailDomains || [])
        .some((domain) => isOfficialDomainMatch(emailDomain, domain));
  }
  if ([
    CHANNEL_TYPES.OFFICIAL_FORM,
    CHANNEL_TYPES.ONLINE_ACCOUNT,
    CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE,
    CHANNEL_TYPES.ESCALATION,
  ].includes(channel.type)) {
    return isOfficialDomainUrl(channel.value, institution.officialDomains)
      && isOfficialDomainMatch(new URL(channel.value).hostname, channel.officialDomain);
  }
  return [CHANNEL_TYPES.PHONE, CHANNEL_TYPES.POSTAL].includes(channel.type)
    && String(channel.value).trim().length > 0;
}

export function getEffectiveChannelStatus(
  channel,
  { now = new Date(), reviewIntervalDays = CHANNEL_REVIEW_INTERVAL_DAYS } = {}
) {
  if (!Object.values(CHANNEL_STATUSES).includes(channel?.status)) {
    return CHANNEL_STATUSES.UNAVAILABLE;
  }
  if (channel.status !== CHANNEL_STATUSES.VERIFIED) return channel.status;
  if (!isIsoDate(channel.verifiedAt)) return CHANNEL_STATUSES.PENDING_REVIEW;
  const verifiedTime = Date.parse(`${channel.verifiedAt}T00:00:00Z`);
  const nowTime = now instanceof Date ? now.getTime() : new Date(now).getTime();
  if (!Number.isFinite(nowTime)) return CHANNEL_STATUSES.PENDING_REVIEW;
  const ageDays = Math.floor((nowTime - verifiedTime) / 86400000);
  return ageDays > reviewIntervalDays
    ? CHANNEL_STATUSES.STALE
    : CHANNEL_STATUSES.VERIFIED;
}

export function isVerifiedChannel(channel, institution, options = {}) {
  return Boolean(
    isVerifiedInstitution(institution)
    && getEffectiveChannelStatus(channel, options) === CHANNEL_STATUSES.VERIFIED
    && channel.confidence === CHANNEL_CONFIDENCE.HIGH
    && channel.valueVerifiedFromSource === true
    && channel.sourceTitle
    && channel.verifiedBy
    && isIsoDate(channel.verifiedAt)
    && isOfficialDomainUrl(channel.sourceUrl, institution.officialDomains)
    && isChannelValueValid(channel, institution)
    && Array.isArray(channel.issueTypes)
    && channel.issueTypes.length > 0
    && Array.isArray(channel.serviceScopes)
    && Array.isArray(channel.billingProviders)
    && Array.isArray(channel.commerceProblems)
    && Array.isArray(channel.purchaseAccountModes)
    && (channel.actionUrl === null || isOfficialDomainUrl(channel.actionUrl, institution.officialDomains))
    && Array.isArray(channel.requirements)
    && channel.requirements.every((requirement) => (
      requirement?.code
      && requirement.label
      && requirement.severity
      && Array.isArray(requirement.appliesTo)
    ))
  );
}

function normalizeIssueType(issueType) {
  return CHANNEL_PURPOSES[issueType] || String(issueType || "");
}

function isServiceScopeApplicable(channel, institution, serviceScope) {
  if (!serviceScope) return true;
  if (institution.serviceScopes?.length && !institution.serviceScopes.includes(serviceScope)) {
    return false;
  }
  return channel.serviceScopes.length === 0 || channel.serviceScopes.includes(serviceScope);
}

function isBillingProviderApplicable(channel, institution, billingProvider) {
  if (institution.category !== "digital") return true;
  if (!billingProvider) return false;
  return channel.billingProviders.length === 0
    || channel.billingProviders.includes(billingProvider);
}

function isCommerceContextApplicable(channel, institution, options = {}) {
  if (institution.category !== "ecommerce") return true;
  if (!options.commerceProblem) return false;
  if (channel.commerceProblems.length > 0 && !channel.commerceProblems.includes(options.commerceProblem)) {
    return false;
  }
  if (institution.companyId !== "pttavm") return true;
  if (!options.purchaseAccountMode) return false;
  return channel.purchaseAccountModes.length === 0
    || channel.purchaseAccountModes.includes(options.purchaseAccountMode);
}

function getApplicableVerifiedChannels(issueType, institution, options = {}) {
  if (!isVerifiedInstitution(institution)) return [];
  const normalizedIssue = normalizeIssueType(issueType);
  return institution.channels
    .filter((channel) => (
      channel.issueTypes.includes(normalizedIssue)
      && isServiceScopeApplicable(channel, institution, options.serviceScope)
      && isBillingProviderApplicable(channel, institution, options.billingProvider)
      && isCommerceContextApplicable(channel, institution, options)
      && isVerifiedChannel(channel, institution, options)
    ))
    .sort((left, right) => (
      (DIRECT_TYPE_RANK[left.type] || 99) - (DIRECT_TYPE_RANK[right.type] || 99)
      || (left.priority || 100) - (right.priority || 100)
    ));
}

export function normalizeInstitutionName(value) {
  return String(value || "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/[ıİ]/g, "i")
    .replace(/[şŞ]/g, "s")
    .replace(/[ğĞ]/g, "g")
    .replace(/[üÜ]/g, "u")
    .replace(/[öÖ]/g, "o")
    .replace(/[çÇ]/g, "c")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function findInstitutionChannel(companyIdOrName, registry = INSTITUTION_CHANNELS) {
  const normalized = normalizeInstitutionName(companyIdOrName);
  if (!normalized) return null;
  return registry.find((institution) => (
    normalizeInstitutionName(institution.companyId) === normalized
    || [institution.companyName, ...(institution.aliases || [])]
      .some((alias) => normalizeInstitutionName(alias) === normalized)
  )) || null;
}

function sourceSummary(channel) {
  if (!channel) return null;
  return {
    url: channel.sourceUrl,
    title: channel.sourceTitle,
    officialDomain: channel.officialDomain,
    verifiedAt: channel.verifiedAt,
    verifiedBy: channel.verifiedBy,
    confidence: channel.confidence,
    status: channel.status,
    actionUrl: channel.actionUrl,
  };
}

function agentMessageFor(institution, primaryChannel, reviewChannels, options) {
  if (institution.category === "digital") {
    if (!primaryChannel && reviewChannels.length > 0) {
      return "Doğrulanmış bir üyelik yönetimi veya iptal action URL'si bulunmadığı için işlem kanalı önermiyorum; tahminî adres kullanılmıyor.";
    }
    if (primaryChannel) {
      return "Faturalandırma sağlayıcınıza uygun doğrulanmış resmî işlem sayfasını açabilirsiniz. Giriş, iptal veya iade adımını siz tamamlarsınız.";
    }
    return "Doğrulanmış bir dijital üyelik işlem kanalı bulunmadığı için tahminî adres önermiyorum.";
  }
  if (institution.category === "ecommerce") {
    if (primaryChannel?.type === CHANNEL_TYPES.EMAIL) {
      return "Misafir sipariş bağlamına özel doğrulanmış destek e-postasını kullanabilirsiniz. Talebi siz gönderirsiniz.";
    }
    if (primaryChannel) {
      return "Sipariş veya iade sorununuz için doğrulanmış resmî platform kanalını açabilirsiniz. Giriş ve talep adımlarını siz tamamlarsınız.";
    }
    return "Doğru e-ticaret kanalını belirlemek için önce sipariş sorununuzu ve gerekiyorsa sipariş türünü seçin; tahminî adres önermiyorum.";
  }
  if (reviewChannels.some((channel) => getEffectiveChannelStatus(channel, options) === CHANNEL_STATUSES.STALE)) {
    return "Bu kanal daha önce doğrulanmış ancak yeniden kontrol edilmesi gerekiyor. Güncel resmî seçenekleri öne aldım.";
  }
  if (primaryChannel?.type === CHANNEL_TYPES.EMAIL) {
    return `${institution.companyName} için kayıtlı doğrulanmış fesih kanalını kontrol ettim. Başvurunuzu göndermeye hazırsınız.`;
  }
  if (primaryChannel?.purpose === "cancellation_pre_application") {
    return "Bu kanal yalnız iptal ön başvurusu içindir; kesin fesih tamamlandı anlamına gelmez.";
  }
  if (primaryChannel?.type === CHANNEL_TYPES.OFFICIAL_FORM) {
    return "Bu kurum için doğrulanmış doğrudan e-posta yerine resmî başvuru formu bulunuyor. Metninizi hazırladım.";
  }
  if (primaryChannel?.type === CHANNEL_TYPES.ONLINE_ACCOUNT) {
    return "Bu kurum için doğrulanmış online işlemler kanalı bulunuyor. Metninizi hazırladım.";
  }
  if (primaryChannel?.type === CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE) {
    return "Bu kurum için doğrulanmış resmî iletişim sayfasını öne aldım.";
  }
  return "Doğrulanmış bir kurum adresi bulamadığım için tahminî bir adres önermiyorum.";
}

export function resolveInstitutionChannel(companyIdOrName, issueType, options = {}) {
  const registry = options.registry || INSTITUTION_CHANNELS;
  const requestedInstitution = findInstitutionChannel(companyIdOrName, registry);
  const normalizedIssue = normalizeIssueType(issueType);
  if (!requestedInstitution) {
    return {
      institution: null,
      issueType: normalizedIssue,
      primaryChannel: null,
      alternativeChannels: [],
      escalationChannels: [],
      reviewChannels: [],
      unavailableReason: "institution_not_found",
      verificationStatus: CHANNEL_STATUSES.UNAVAILABLE,
      source: null,
      verifiedAt: null,
      agentMessage: "Kurum kanal kaydı bulunmadığı için tahminî bir adres önermiyorum.",
      reviewRequired: false,
      serviceScope: options.serviceScope || null,
      billingProvider: options.billingProvider || null,
    };
  }

  const billingProvider = options.billingProvider || null;
  const isDigital = requestedInstitution.category === "digital";
  const isEcommerce = requestedInstitution.category === "ecommerce";
  const providerTargetId = billingProvider === "google_play"
    ? "google-play"
    : billingProvider === "apple"
      ? "apple-app-store"
      : null;
  const routedInstitution = providerTargetId
    ? findInstitutionChannel(providerTargetId, registry)
    : requestedInstitution;
  const partnerMessage = billingProvider === "telecom_partner"
    ? "Ödeme telefon veya internet faturanıza yansıyorsa iptal ya da iade için ilgili operatörün veya iş ortağının resmî kanalını kullanın."
    : billingProvider === "other_partner"
      ? "Abonelik başka bir iş ortağı tarafından faturalandırılıyorsa işlemi o sağlayıcının resmî kanalında tamamlayın."
      : billingProvider === "unknown"
        ? "Önce kart ekstrenizden veya hesap abonelik bölümünden faturalandırma sağlayıcısını kontrol edin; yanlış platforma yönlendirme yapmıyorum."
        : !billingProvider
          ? "Doğru resmî kanalı belirlemek için önce faturalandırma sağlayıcısını seçin."
          : null;
  if (isDigital && (partnerMessage || !routedInstitution)) {
    return {
      institution: requestedInstitution,
      requestedInstitution,
      issueType: normalizedIssue,
      primaryChannel: null,
      alternativeChannels: [],
      escalationChannels: [],
      reviewChannels: [],
      unavailableReason: !billingProvider ? "billing_provider_required" : "billing_provider_external",
      verificationStatus: CHANNEL_STATUSES.UNAVAILABLE,
      source: null,
      verifiedAt: null,
      agentMessage: partnerMessage,
      reviewRequired: false,
      serviceScope: options.serviceScope || null,
      billingProvider,
    };
  }

  if (isEcommerce && (!options.commerceProblem
    || (requestedInstitution.companyId === "pttavm" && !options.purchaseAccountMode))) {
    return {
      institution: requestedInstitution,
      requestedInstitution,
      issueType: normalizedIssue,
      primaryChannel: null,
      alternativeChannels: [],
      escalationChannels: [],
      reviewChannels: [],
      unavailableReason: !options.commerceProblem ? "commerce_problem_required" : "purchase_account_mode_required",
      verificationStatus: CHANNEL_STATUSES.UNAVAILABLE,
      source: null,
      verifiedAt: null,
      agentMessage: "Doğru resmî kanalı belirlemek için önce sipariş sorununuzu ve gerekiyorsa siparişi hesapla mı misafir olarak mı verdiğinizi seçin.",
      reviewRequired: false,
      serviceScope: options.serviceScope || null,
      billingProvider,
      commerceProblem: options.commerceProblem || null,
      purchaseAccountMode: options.purchaseAccountMode || null,
    };
  }

  const institution = routedInstitution;
  const effectiveServiceScope = providerTargetId
    ? institution.serviceScopes?.[0] || null
    : options.serviceScope;
  const effectiveOptions = {
    ...options,
    serviceScope: effectiveServiceScope,
    billingProvider,
  };

  const issueChannels = institution.channels
    .filter((channel) => (
      channel.issueTypes.includes(normalizedIssue)
      && isServiceScopeApplicable(channel, institution, effectiveServiceScope)
      && isBillingProviderApplicable(channel, institution, billingProvider)
      && isCommerceContextApplicable(channel, institution, effectiveOptions)
    ));
  const verifiedChannels = getApplicableVerifiedChannels(normalizedIssue, institution, effectiveOptions);
  const directChannels = verifiedChannels.filter((channel) => (
    !channel.alternativeOnly
    && channel.type !== CHANNEL_TYPES.ESCALATION
    && PRIMARY_CHANNEL_TYPES.has(channel.type)
  ));
  const supportingChannels = verifiedChannels.filter((channel) => (
    !channel.alternativeOnly
    && [CHANNEL_TYPES.PHONE, CHANNEL_TYPES.POSTAL].includes(channel.type)
  ));
  const escalationChannels = verifiedChannels.filter((channel) => (
    channel.alternativeOnly || channel.type === CHANNEL_TYPES.ESCALATION
  ));
  const reviewChannels = issueChannels.filter((channel) => {
    const effectiveStatus = getEffectiveChannelStatus(channel, effectiveOptions);
    return effectiveStatus === CHANNEL_STATUSES.STALE
      || effectiveStatus === CHANNEL_STATUSES.PENDING_REVIEW;
  });
  const primaryChannel = directChannels[0] || null;
  const unavailableReason = primaryChannel
    ? null
    : reviewChannels.length
      ? "channels_require_review"
      : effectiveServiceScope
        && institution.serviceScopes?.length
        && !institution.serviceScopes.includes(effectiveServiceScope)
        ? "service_scope_not_supported"
      : institution.issueTypes.includes(normalizedIssue)
        ? "no_verified_direct_channel"
        : "issue_not_supported";
  const verificationStatus = primaryChannel
    ? CHANNEL_STATUSES.VERIFIED
    : reviewChannels.some((channel) => getEffectiveChannelStatus(channel, effectiveOptions) === CHANNEL_STATUSES.STALE)
      ? CHANNEL_STATUSES.STALE
      : reviewChannels.length
        ? CHANNEL_STATUSES.PENDING_REVIEW
        : CHANNEL_STATUSES.UNAVAILABLE;

  return {
    institution,
    issueType: normalizedIssue,
    primaryChannel,
    alternativeChannels: [...directChannels.slice(1), ...supportingChannels],
    escalationChannels,
    reviewChannels,
    unavailableReason,
    verificationStatus,
    source: sourceSummary(primaryChannel),
    verifiedAt: primaryChannel?.verifiedAt || null,
    agentMessage: agentMessageFor(institution, primaryChannel, reviewChannels, effectiveOptions),
    reviewRequired: reviewChannels.length > 0,
    serviceScope: options.serviceScope || null,
    routedServiceScope: effectiveServiceScope || null,
    billingProvider,
    commerceProblem: options.commerceProblem || null,
    purchaseAccountMode: options.purchaseAccountMode || null,
    requestedInstitution,
  };
}

// Geriye dönük yardımcılar mevcut Workflow sözleşmesini korur; tüm güven kararı
// yukarıdaki tek resolver/doğrulama katmanından gelir.
export function getVerifiedRecipient(issueType, institution, options = {}) {
  return getApplicableVerifiedChannels(issueType, institution, options)
    .find((channel) => channel.type === CHANNEL_TYPES.EMAIL && !channel.alternativeOnly)?.value || null;
}

export function getOfficialApplicationChannels(issueType, institution, options = {}) {
  const channels = getApplicableVerifiedChannels(issueType, institution, options)
    .filter((channel) => channel.type !== CHANNEL_TYPES.EMAIL);
  if (normalizeIssueType(issueType) !== "hakem") return channels;
  const globalInstitution = {
    companyId: "global-public-services",
    companyName: "Kamu başvuru hizmetleri",
    officialDomains: ["turkiye.gov.tr"],
    issueTypes: ["hakem"],
    channels: [VERIFIED_GLOBAL_CHANNELS.consumerArbitration],
  };
  return isVerifiedChannel(VERIFIED_GLOBAL_CHANNELS.consumerArbitration, globalInstitution, options)
    ? [VERIFIED_GLOBAL_CHANNELS.consumerArbitration, ...channels]
    : channels;
}

export function getDirectApplicationChannels(issueType, institution, options = {}) {
  return getApplicableVerifiedChannels(issueType, institution, options)
    .filter((channel) => (
      !channel.alternativeOnly
      && channel.type !== CHANNEL_TYPES.EMAIL
      && channel.type !== CHANNEL_TYPES.ESCALATION
    ));
}

export function getInstitutionDeliveryProfile(issueType, companyIdOrName, options = {}) {
  const resolution = resolveInstitutionChannel(companyIdOrName, issueType, options);
  if (!resolution.institution) return null;
  const { institution, primaryChannel } = resolution;
  return {
    companyId: institution.companyId,
    name: institution.companyName,
    issueType: resolution.issueType,
    channelType: primaryChannel?.type || null,
    email: primaryChannel?.type === CHANNEL_TYPES.EMAIL ? primaryChannel.value : null,
    officialUrl: primaryChannel && primaryChannel.type !== CHANNEL_TYPES.EMAIL
      ? primaryChannel.value
      : null,
    alternative: resolution.escalationChannels.map((channel) => ({
      type: channel.type,
      label: channel.label,
      officialUrl: channel.value,
      verifiedAt: channel.verifiedAt,
    })),
    requiredFields: ["firstName", "lastName", "email", "phone", "subscriptionNumber"],
    acceptsAttachments: primaryChannel?.acceptsAttachments ?? null,
    notes: institution.notes || "",
    verifiedAt: primaryChannel?.verifiedAt || null,
    verificationStatus: resolution.verificationStatus,
    source: resolution.source,
    requirements: primaryChannel?.requirements || [],
    purpose: primaryChannel?.purpose || null,
    serviceScopes: primaryChannel?.serviceScopes || [],
    selectedServiceScope: resolution.serviceScope,
    billingProvider: resolution.billingProvider,
    requestedCompanyId: resolution.requestedInstitution?.companyId || institution.companyId,
  };
}

export function getVerifiedOfficialSite(institution, options = {}) {
  const channel = getApplicableVerifiedChannels("iptal", institution, options)
    .find((candidate) => candidate.type === CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE);
  return channel ? { url: channel.value, label: channel.label } : null;
}
